import {
  type ControllerParams,
  type CreateControllerFn,
} from '@wix/yoshi-flow-editor';
import { type AdUnit } from '@wix/ambassador-adsense-v1-adsense-adunit/types';
import {
  ConnectStatus,
  type User,
} from '@wix/ambassador-adsense-v1-adsense-user/types';
import { fetchCachedUser } from '@app/services';
import { findAdUnitId } from '@app/utils';

/**
 * Editor and live site use the same API: fetchCachedUser only.
 * Ad unit is derived from user.adsenseInfo.connectedAdunits (no fetchAdUnitByCompId).
 */
const createController: CreateControllerFn = async ({
  flowAPI,
  controllerConfig,
}: ControllerParams) => {
  const { setProps, compId } = controllerConfig;
  const { instanceId } = controllerConfig.appParams;
  const { httpClient } = flowAPI;

  const getWidgetData = async (): Promise<{
    user: User | null;
    adUnitByCompId: AdUnit | undefined;
  }> => {
    try {
      const userData = await fetchCachedUser(httpClient, instanceId);
      const user = userData?.adsenseUser?.user ?? null;

      if (!user) {
        return { user: null, adUnitByCompId: undefined };
      }

      const reportingDimensionId = findAdUnitId(
        compId,
        user.adsenseInfo?.connectedAdunits,
      );

      const adUnitByCompId: AdUnit | undefined = reportingDimensionId
        ? { reportingDimensionId }
        : undefined;

      return { user, adUnitByCompId };
    } catch (error) {
      console.error('Error fetching widget data:', error);
      return { user: null, adUnitByCompId: undefined };
    }
  };

  const getConnectStatus = (user: User | null): ConnectStatus => {
    return user?.adsenseInfo?.connectStatus ?? ConnectStatus.DISCONNECTED;
  };

  return {
    async pageReady() {
      const { user, adUnitByCompId } = await getWidgetData();

      setProps({
        connectStatus: getConnectStatus(user),
        adUnitByCompId,
      });
    },
  };
};

export default createController;
